import React from 'react';
import { FileText, Shield, AlertCircle, Mail, Scale } from 'lucide-react';

/**
 * Terms & Conditions for ELR Industries ERP / Android App.
 * Published at /terms and /terms-and-conditions
 * All app-related communications: elrinfra@gmail.com
 */
export default function TermsAndConditions() {
  const lastUpdated = '27 August 2026';
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#1F7A6E] flex items-center justify-center">
              <Scale size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-[#1F7A6E]">ELR Industries Pvt Ltd</div>
              <h1 className="font-display font-bold text-base text-slate-900">Terms &amp; Conditions</h1>
            </div>
          </div>
          <a href="https://erp.elrindia.com" className="text-xs text-[#1F7A6E] font-display font-semibold hover:underline">erp.elrindia.com</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8" data-testid="terms-page">
        <p className="text-xs text-slate-500 mb-6">Last updated: {lastUpdated}</p>

        <Section icon={FileText} title="1. Acceptance of Terms">
          <p>These Terms and Conditions ("Terms") govern your access to and use of the <strong>ELR Industries ERP</strong> application ("App"), operated by <strong>ELR Industries Pvt Ltd</strong> ("ELR Industries", "we", "us", "our"), available at <a className="text-[#1F7A6E]" href="https://erp.elrindia.com">https://erp.elrindia.com</a> and on the Google Play Store.</p>
          <p>By installing, accessing, or using the App, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree, do not use the App.</p>
          <p>Access to the App is granted to <strong>authorised users only</strong> — employees, contractors, vendors, and site staff of ELR Industries. Unauthorised access or use is strictly prohibited.</p>
        </Section>

        <Section icon={Shield} title="2. Eligibility &amp; Account Access">
          <ul>
            <li>You must be at least 18 years old and have an active employment, contractor, or vendor relationship with ELR Industries to access the App.</li>
            <li>Your account credentials are personal to you. You must not share your password or allow others to access the App using your credentials.</li>
            <li>You are responsible for all activity that occurs under your account. Notify <a className="text-[#1F7A6E]" href="mailto:elrinfra@gmail.com">elrinfra@gmail.com</a> immediately if you suspect unauthorised access.</li>
            <li>ELR Industries reserves the right to suspend or terminate your access at any time, without notice, if you violate these Terms or your engagement with the company ends.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="3. Permitted Use">
          <p>The App is provided exclusively for ELR Industries' internal business operations. You agree to use the App only for its intended purpose and in compliance with applicable Indian laws.</p>
          <p>You must <strong>not</strong>:</p>
          <ul>
            <li>Use the App to access, collect, or export data beyond your authorised role and permissions.</li>
            <li>Attempt to reverse-engineer, decompile, or tamper with the App or its backend systems.</li>
            <li>Introduce malware, viruses, or any harmful code into the App or its infrastructure.</li>
            <li>Use the App for any unlawful, fraudulent, or unauthorised purpose.</li>
            <li>Share confidential company data obtained through the App with third parties without authorisation.</li>
            <li>Circumvent, disable, or interfere with the App's security features or access controls.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="4. Data &amp; Confidentiality">
          <p>All data you access through the App — including production data, financial records, HR information, project details, and customer information — is confidential property of ELR Industries Pvt Ltd.</p>
          <ul>
            <li>You agree to maintain the confidentiality of all data accessed through the App during and after your engagement with ELR Industries.</li>
            <li>Data entered by you into the App remains the property of ELR Industries.</li>
            <li>Our collection and use of personal data is governed by our <a className="text-[#1F7A6E]" href="/privacy">Privacy Policy</a>, which is incorporated into these Terms by reference.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="5. Intellectual Property">
          <p>The App, including all software, design, content, features, and functionality, is owned by ELR Industries Pvt Ltd and is protected by applicable intellectual property laws.</p>
          <ul>
            <li>No part of the App may be copied, reproduced, distributed, or adapted without prior written consent from ELR Industries.</li>
            <li>You are granted a limited, non-exclusive, non-transferable licence to use the App solely for its intended business purpose during your authorised engagement.</li>
            <li>This licence terminates automatically upon the end of your engagement with ELR Industries or upon violation of these Terms.</li>
          </ul>
        </Section>

        <Section icon={AlertCircle} title="6. Disclaimer of Warranties">
          <p>The App is provided <strong>"as is"</strong> and <strong>"as available"</strong> without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or uninterrupted operation.</p>
          <p>ELR Industries does not warrant that the App will be error-free, virus-free, or available at all times. Scheduled maintenance, updates, and unforeseen outages may temporarily affect availability.</p>
        </Section>

        <Section icon={Scale} title="7. Limitation of Liability">
          <p>To the maximum extent permitted by applicable law, ELR Industries shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of or inability to use the App, including loss of data, business interruption, or unauthorised access.</p>
          <p>ELR Industries' total aggregate liability to you shall not exceed the amount you paid (if any) for access to the App in the preceding three months.</p>
        </Section>

        <Section icon={FileText} title="8. Updates to the App &amp; Terms">
          <ul>
            <li>ELR Industries may update, modify, or discontinue the App at any time without prior notice.</li>
            <li>These Terms may be revised from time to time. Continued use of the App after any change constitutes acceptance of the revised Terms.</li>
            <li>Material changes will be notified within the App or via email to active users.</li>
          </ul>
        </Section>

        <Section icon={Scale} title="9. Governing Law &amp; Dispute Resolution">
          <p>These Terms are governed by the laws of the <strong>Republic of India</strong>. Any disputes arising out of or in connection with these Terms or the App shall be subject to the exclusive jurisdiction of the courts of <strong>Pune, Maharashtra, India</strong>.</p>
          <p>Before initiating legal proceedings, the parties agree to attempt resolution through good-faith negotiation for a period of 30 days.</p>
        </Section>

        <Section icon={Mail} title="10. Contact &amp; Support">
          <p>For any questions about these Terms, technical support, or app-related communications:</p>
          <div className="bg-white border border-slate-200 rounded-sm p-4 my-3 text-sm">
            <div className="font-display font-bold text-slate-900">ELR Industries Pvt Ltd</div>
            <div className="text-slate-600 mt-1">App Support &amp; Legal Enquiries</div>
            <div className="text-slate-600 mt-2">Email: <a className="text-[#1F7A6E] font-semibold" href="mailto:elrinfra@gmail.com">elrinfra@gmail.com</a></div>
            <div className="text-slate-600">Website: <a className="text-[#1F7A6E] font-semibold" href="https://erp.elrindia.com" target="_blank" rel="noreferrer">https://erp.elrindia.com</a></div>
          </div>
        </Section>

        <footer className="border-t border-slate-200 mt-10 pt-6 pb-12 text-xs text-slate-500 flex flex-wrap gap-4 items-center justify-between">
          <span>© {new Date().getFullYear()} ELR Industries Pvt Ltd. All rights reserved. Governed by the laws of India.</span>
          <a href="/privacy" className="text-[#1F7A6E] hover:underline font-medium">&larr; Privacy Policy</a>
        </footer>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="flex items-center gap-2 font-display font-bold text-base text-slate-900 mb-3 mt-6">
        <Icon size={15} className="text-[#1F7A6E]" />{title}
      </h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
        {children}
      </div>
    </section>
  );
}
